// TEMPORARY MOCK ADAPTER for Role Management.
// In-memory only — state lives in this module's `roles` array and resets on hard reload.
// Mutations resolve with ~300 ms simulated latency. Errors mimic the backend envelope.
// Backed switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter. See role.adapter.ts.

import {
  MENU_CATEGORY_KEYS,
  RoleApiError,
  type CreateRoleInput,
  type DeleteRoleResult,
  type MenuCategoryKey,
  type PermissionMap,
  type PermissionValue,
  type Role,
  type RoleApi,
  type UpdateRoleInput,
} from '../types/role.types';

const LATENCY_MS = 300;
const ROLE_NAME_REGEX = /^[A-Z0-9_]{2,30}$/;

const allOf = (value: PermissionValue): PermissionMap =>
  MENU_CATEGORY_KEYS.reduce<PermissionMap>((acc, key) => {
    acc[key] = value;
    return acc;
  }, {} as PermissionMap);

const seedMap = (...overrides: Array<[MenuCategoryKey, PermissionValue]>): PermissionMap => {
  const map = allOf('NO_PERMISSION');
  for (const [k, v] of overrides) map[k] = v;
  return map;
};

const now = () => new Date().toISOString();
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

let nextId = 6;

const roles: Role[] = [
  {
    id: 1,
    name: 'ROOT',
    permissionMap: allOf('WRITE'),
    isReserved: true,
    isSeed: false,
    adminCount: 1,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
    createdBy: { id: null, name: 'System' },
  },
  {
    id: 2,
    name: 'OPERATION',
    permissionMap: seedMap(
      ['DASHBOARD', 'READ'],
      ['PATIENT', 'WRITE'],
      ['STAFF', 'WRITE'],
      ['PRO_MGMT', 'READ'],
      ['CUSTOMER_SUPPORT', 'WRITE'],
    ),
    isReserved: false,
    isSeed: true,
    adminCount: 3,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
    createdBy: { id: null, name: 'System' },
  },
  {
    id: 3,
    name: 'CONTENT',
    permissionMap: seedMap(
      ['DASHBOARD', 'READ'],
      ['PRO_MGMT', 'WRITE'],
      ['METADATA', 'WRITE'],
      ['CONTENT_LIB', 'WRITE'],
      ['PUBLICATION', 'WRITE'],
    ),
    isReserved: false,
    isSeed: true,
    adminCount: 2,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
    createdBy: { id: null, name: 'System' },
  },
  {
    id: 4,
    name: 'SYSTEM',
    permissionMap: seedMap(['DASHBOARD', 'READ'], ['SYSTEM_MGMT', 'WRITE']),
    isReserved: false,
    isSeed: true,
    adminCount: 1,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
    createdBy: { id: null, name: 'System' },
  },
  {
    id: 5,
    name: 'DYNAMIC_ROLE',
    permissionMap: seedMap(
      ['DASHBOARD', 'READ'],
      ['CONTENT_LIB', 'WRITE'],
      ['PUBLICATION', 'READ'],
    ),
    isReserved: false,
    isSeed: false,
    adminCount: 0,
    createdAt: '2026-04-01T09:30:00.000Z',
    updatedAt: '2026-04-15T16:20:00.000Z',
    createdBy: { id: 12, name: 'Root 관리자' },
  },
];

const findRole = (id: number): Role => {
  const role = roles.find((r) => r.id === id);
  if (!role) throw new RoleApiError('NOT_FOUND', '해당 Role을 찾을 수 없습니다.');
  return role;
};

const validateName = (name: string): void => {
  if (!name.trim()) {
    throw new RoleApiError('ROLE_NAME_INVALID', 'Role 이름을 입력해주세요.', {
      name: 'Role 이름을 입력해주세요.',
    });
  }
  if (!ROLE_NAME_REGEX.test(name)) {
    throw new RoleApiError('ROLE_NAME_INVALID', '영문 대문자·숫자·_ 로 2~30자 입력해주세요.', {
      name: '영문 대문자·숫자·_ 로 2~30자 입력해주세요.',
    });
  }
};

const ensureUniqueName = (name: string, excludeId?: number): void => {
  const taken = roles.some((r) => r.name === name && r.id !== excludeId);
  if (taken) {
    throw new RoleApiError('ROLE_NAME_DUPLICATE', '이미 존재하는 Role 이름입니다.', {
      name: '이미 존재하는 Role 이름입니다.',
    });
  }
};

export const roleMockApi: RoleApi = {
  async listRoles() {
    await delay();
    return clone(roles);
  },

  async getRole(id) {
    await delay();
    return clone(findRole(id));
  },

  async createRole(input: CreateRoleInput) {
    await delay();
    validateName(input.name);
    ensureUniqueName(input.name);

    const created: Role = {
      id: nextId++,
      name: input.name,
      permissionMap: { ...input.permissionMap },
      isReserved: false,
      isSeed: false,
      adminCount: 0,
      createdAt: now(),
      updatedAt: now(),
      createdBy: { id: 12, name: 'Root 관리자' },
    };
    roles.push(created);
    return clone(created);
  },

  async updateRole(id, input: UpdateRoleInput) {
    await delay();
    const role = findRole(id);
    if (role.isReserved) {
      throw new RoleApiError('RESERVED_ROLE', '시스템 예약 Role은 편집할 수 없습니다.');
    }
    if (input.name !== undefined) {
      validateName(input.name);
      ensureUniqueName(input.name, role.id);
      role.name = input.name;
    }
    if (input.permissionMap !== undefined) {
      role.permissionMap = { ...input.permissionMap };
    }
    role.updatedAt = now();
    return clone(role);
  },

  async deleteRole(id): Promise<DeleteRoleResult> {
    await delay();
    const role = findRole(id);
    if (role.isReserved) {
      throw new RoleApiError('RESERVED_ROLE', 'Root Role은 삭제할 수 없습니다.');
    }
    if (role.isSeed) {
      throw new RoleApiError('SEED_ROLE_NOT_DELETABLE', '시드/예약 Role은 삭제할 수 없습니다.');
    }
    const affectedAdminIds = Array.from({ length: role.adminCount }, (_, i) => 100 + i);
    const index = roles.indexOf(role);
    roles.splice(index, 1);
    return { affectedAdminIds, affectedCount: role.adminCount };
  },
};
